import { useWorkspace } from "@frontend/contexts/WorkspaceContext";
import { useMyStands } from "@frontend/modules/exhibitor/StandGate";
import { useTenant } from "@frontend/tenant/TenantContext";
import { orpc } from "@frontend/utils/orpc.tanstack.client";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";

/**
 * Whether home is the person's stands rather than the team's (root ADR-036):
 * always for a team's exhibitor, and on their own team while it holds stands
 * elsewhere and has organised nothing yet. `null` while that is being read.
 * `myEvents` opens their own team's events — switching to it first — and is
 * absent when they have no own team. On a team's subdomain it is that
 * team's events, offered only to someone who is staff there, not its
 * exhibitor: the subdomain is one team's app (root ADR-017). Read by the
 * dashboard and the navbar, which carries the "My events" link beside Home.
 */
export function useStandsHome() {
	const { user, teams, activeWorkspace, setActiveTeam } = useWorkspace();
	const { isTenant } = useTenant();
	const navigate = useNavigate();
	const exhibitorHere = activeWorkspace.role === "Exhibitor";
	const ownTeam = activeWorkspace.type === "personal";
	const { stands, isLoading: standsLoading } = useMyStands();
	const { data: events, isLoading: eventsLoading } = useQuery({
		...orpc.events.list.queryOptions(),
		enabled: !exhibitorHere && ownTeam && !!activeWorkspace.id,
	});

	const show: boolean | null = exhibitorHere
		? true
		: !ownTeam
			? false
			: standsLoading || eventsLoading
				? null
				: stands.length > 0 && events?.length === 0;

	const own = teams.find((t) => user && t.personalTeamForUserId === user.id);
	const myEvents = isTenant
		? exhibitorHere
			? undefined
			: async () => navigate("/events")
		: own
			? async () => {
					if (own.id !== activeWorkspace.id) await setActiveTeam(own.id);
					navigate("/events");
				}
			: undefined;

	return { show, myEvents };
}
